import {
  RecipeV2,
  RecipeV2Detail,
  RecipeComponent,
  RecipeStatus,
  RecipeKind,
  AllergenStatus,
  AllergenSource,
  ApprovalStatus,
  AIFindingSeverity,
  AIFindingStatus,
  CarbonRating,
  Allergen,
  ProductionMethod,
  RecipeAllergenSummary,
  DietaryAttribute,
  DietaryCategory,
  DietaryAttributeStatus,
  RecipeStep,
} from '../../pages/RecipeV2/types';
import { mockRecipes, MockRecipe, getRecipeComponentsForApi } from './itemAndComponentData';
import { commonAllergens } from './allergensData';

// Shared allergen reference list (see allergensData.ts), reused across allergen capture screens.
export const mockAllergens: Allergen[] = commonAllergens;

export const mockDietaryAttributes: Record<string, DietaryAttribute[]> = {
  // Pizza Margherita: contains allergens + vegetarian + not vegan (dairy)
  'RCP-003': [
    { id: 100, code: 'GLUTEN', name: 'Cereals containing gluten', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.Contains, source: AllergenSource.Manual, regulatory: true },
    { id: 101, code: 'MILK', name: 'Milk', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.Contains, source: AllergenSource.Manual, regulatory: true },
    { id: 102, code: 'SOYBEANS', name: 'Soybeans', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.MayContain, source: AllergenSource.AI, regulatory: true },
    { id: 103, code: 'LACTOSE', name: 'Lactose', category: DietaryCategory.Intolerance, status: DietaryAttributeStatus.Contains, source: AllergenSource.Manual },
    { id: 104, code: 'VEGETARIAN', name: 'Vegetarian', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 105, code: 'VEGAN', name: 'Vegan', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.NotSuitable, source: AllergenSource.Manual },
    { id: 106, code: 'HALAL', name: 'Halal', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 107, code: 'KETO', name: 'Keto', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.NotSuitable, source: AllergenSource.Manual },
    { id: 108, code: 'LOW_CARB', name: 'Low-Carb', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.NotSuitable, source: AllergenSource.Manual },
    { id: 109, code: 'GLUTEN_FREE', name: 'Gluten-Free', category: DietaryCategory.NutritionalClaim, status: DietaryAttributeStatus.NotSuitable, source: AllergenSource.Manual, regulatory: true },
    { id: 110, code: 'DAIRY_FREE', name: 'Dairy-Free', category: DietaryCategory.NutritionalClaim, status: DietaryAttributeStatus.NotSuitable, source: AllergenSource.Manual, regulatory: true },
  ],
  // Artisan Bread Dough
  'RCP-001': [
    { id: 200, code: 'GLUTEN', name: 'Cereals containing gluten', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.Contains, source: AllergenSource.Manual, regulatory: true },
    { id: 201, code: 'MILK', name: 'Milk', category: DietaryCategory.Allergen, status: DietaryAttributeStatus.Contains, source: AllergenSource.Manual, regulatory: true },
    { id: 202, code: 'LACTOSE', name: 'Lactose', category: DietaryCategory.Intolerance, status: DietaryAttributeStatus.Contains, source: AllergenSource.Manual },
    { id: 203, code: 'VEGETARIAN', name: 'Vegetarian', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 204, code: 'VEGAN', name: 'Vegan', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.NotSuitable, source: AllergenSource.Manual },
    { id: 205, code: 'HALAL', name: 'Halal', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 206, code: 'KOSHER', name: 'Kosher', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
  ],
  // House Tomato Sauce: allergen-free, suitable for most diets
  'RCP-002': [
    { id: 300, code: 'VEGETARIAN', name: 'Vegetarian', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 301, code: 'VEGAN', name: 'Vegan', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 302, code: 'HALAL', name: 'Halal', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Certified, source: AllergenSource.Manual },
    { id: 303, code: 'KOSHER', name: 'Kosher', category: DietaryCategory.DietaryRequirement, status: DietaryAttributeStatus.Certified, source: AllergenSource.Manual },
    { id: 304, code: 'KETO', name: 'Keto', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 305, code: 'PALEO', name: 'Paleo', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 306, code: 'WHOLE30', name: 'Whole30', category: DietaryCategory.LifestylePreference, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual },
    { id: 307, code: 'GLUTEN_FREE', name: 'Gluten-Free', category: DietaryCategory.NutritionalClaim, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual, regulatory: true },
    { id: 308, code: 'DAIRY_FREE', name: 'Dairy-Free', category: DietaryCategory.NutritionalClaim, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual, regulatory: true },
    { id: 309, code: 'LOW_FAT', name: 'Low-Fat', category: DietaryCategory.NutritionalClaim, status: DietaryAttributeStatus.Suitable, source: AllergenSource.Manual, regulatory: true },
  ],
};

export const mockProductionMethods: ProductionMethod[] = [
  { id: 1, name: 'Raw / No Cook', carbonMultiplier: 1.0, description: 'No additional energy used' },
  { id: 2, name: 'Oven Bake', carbonMultiplier: 1.3, description: 'Conventional oven cooking' },
  { id: 3, name: 'Deep Fry', carbonMultiplier: 1.5, description: 'Oil-based deep frying' },
  { id: 4, name: 'Steam', carbonMultiplier: 1.1, description: 'Steam-based cooking' },
  { id: 5, name: 'Grill', carbonMultiplier: 1.4, description: 'Direct heat grilling' },
  { id: 6, name: 'Sous Vide', carbonMultiplier: 1.2, description: 'Low-temperature vacuum cooking' },
];

const makeBreadComponents: RecipeComponent[] = [
  {
    id: 101, recipeId: 1, componentProductId: 501, componentItemNumber: 'FLR-001',
    componentDescription: 'Strong White Bread Flour', quantity: 1.0, weight: 1000, volume: 0,
    sortOrder: 1, uom: 'KG', costPrice: 1.20, costContribution: 28.5, weightNet: 1000,
    isRecipe: false, allergens: [
      { productId: 501, allergenId: 2, allergenCode: 'GLUTEN', allergenName: 'Cereals containing gluten', status: AllergenStatus.Contains, source: AllergenSource.Manual, updatedBy: 'admin', updatedAt: '2026-01-15' },
    ], carbonFactor: { productId: 501, kgCo2ePerUnit: 0.78, source: 'Open Food Facts', sourceReference: 'off:wheat-flour', lastUpdated: '2026-03-01' },
  },
  {
    id: 102, recipeId: 1, componentProductId: 502, componentItemNumber: 'YST-001',
    componentDescription: 'Dried Active Yeast', quantity: 0.01, weight: 10, volume: 0,
    sortOrder: 2, uom: 'KG', costPrice: 8.50, costContribution: 2.0, weightNet: 500,
    isRecipe: false, allergens: [], carbonFactor: { productId: 502, kgCo2ePerUnit: 1.2, source: 'Open Food Facts', sourceReference: 'off:yeast', lastUpdated: '2026-03-01' },
  },
  {
    id: 103, recipeId: 1, componentProductId: 503, componentItemNumber: 'SLT-001',
    componentDescription: 'Sea Salt', quantity: 0.02, weight: 20, volume: 0,
    sortOrder: 3, uom: 'KG', costPrice: 0.85, costContribution: 0.4, weightNet: 1000,
    isRecipe: false, allergens: [], carbonFactor: { productId: 503, kgCo2ePerUnit: 0.15, source: 'Open Food Facts', sourceReference: 'off:salt', lastUpdated: '2026-03-01' },
  },
  {
    id: 104, recipeId: 1, componentProductId: 504, componentItemNumber: 'BTR-001',
    componentDescription: 'Unsalted Butter', quantity: 0.05, weight: 50, volume: 0,
    sortOrder: 4, uom: 'KG', costPrice: 9.80, costContribution: 11.6, weightNet: 250,
    isRecipe: false, allergens: [
      { productId: 504, allergenId: 7, allergenCode: 'MILK', allergenName: 'Milk', status: AllergenStatus.Contains, source: AllergenSource.Manual, updatedBy: 'admin', updatedAt: '2026-01-15' },
    ], carbonFactor: { productId: 504, kgCo2ePerUnit: 9.8, source: 'Open Food Facts', sourceReference: 'off:butter', lastUpdated: '2026-03-01' },
  },
  {
    id: 105, recipeId: 1, componentProductId: 505, componentItemNumber: 'WTR-001',
    componentDescription: 'Water', quantity: 0.65, weight: 650, volume: 650,
    sortOrder: 5, uom: 'L', costPrice: 0.002, costContribution: 0.03, weightNet: 1000,
    isRecipe: false, allergens: [], carbonFactor: { productId: 505, kgCo2ePerUnit: 0.001, source: 'Manual', sourceReference: 'internal', lastUpdated: '2026-01-01' },
  },
];

const sauceComponents: RecipeComponent[] = [
  {
    id: 201, recipeId: 2, componentProductId: 510, componentItemNumber: 'TOM-001',
    componentDescription: 'Chopped Tomatoes (Tinned)', quantity: 2.0, weight: 800, volume: 0,
    sortOrder: 1, uom: 'KG', costPrice: 0.89, costContribution: 42.3, weightNet: 400,
    isRecipe: false, allergens: [], carbonFactor: { productId: 510, kgCo2ePerUnit: 1.4, source: 'Open Food Facts', sourceReference: 'off:canned-tomatoes', lastUpdated: '2026-03-01' },
  },
  {
    id: 202, recipeId: 2, componentProductId: 511, componentItemNumber: 'GRL-001',
    componentDescription: 'Fresh Garlic', quantity: 0.03, weight: 30, volume: 0,
    sortOrder: 2, uom: 'KG', costPrice: 6.00, costContribution: 4.3, weightNet: 100,
    isRecipe: false, allergens: [], carbonFactor: { productId: 511, kgCo2ePerUnit: 0.46, source: 'Open Food Facts', sourceReference: 'off:garlic', lastUpdated: '2026-03-01' },
  },
  {
    id: 203, recipeId: 2, componentProductId: 512, componentItemNumber: 'ONI-001',
    componentDescription: 'White Onion', quantity: 0.15, weight: 150, volume: 0,
    sortOrder: 3, uom: 'KG', costPrice: 1.10, costContribution: 3.9, weightNet: 200,
    isRecipe: false, allergens: [], carbonFactor: { productId: 512, kgCo2ePerUnit: 0.26, source: 'Open Food Facts', sourceReference: 'off:onion', lastUpdated: '2026-03-01' },
  },
  {
    id: 204, recipeId: 2, componentProductId: 513, componentItemNumber: 'BSL-001',
    componentDescription: 'Fresh Basil', quantity: 0.02, weight: 20, volume: 0,
    sortOrder: 4, uom: 'KG', costPrice: 24.00, costContribution: 11.4, weightNet: 50,
    isRecipe: false, allergens: [], carbonFactor: { productId: 513, kgCo2ePerUnit: 0.4, source: 'Open Food Facts', sourceReference: 'off:basil', lastUpdated: '2026-03-01' },
  },
  {
    id: 205, recipeId: 2, componentProductId: 503, componentItemNumber: 'SLT-001',
    componentDescription: 'Sea Salt', quantity: 0.01, weight: 10, volume: 0,
    sortOrder: 5, uom: 'KG', costPrice: 0.85, costContribution: 0.2, weightNet: 1000,
    isRecipe: false, allergens: [], carbonFactor: { productId: 503, kgCo2ePerUnit: 0.15, source: 'Open Food Facts', sourceReference: 'off:salt', lastUpdated: '2026-03-01' },
  },
];

const pizzaComponents: RecipeComponent[] = [
  {
    id: 301, recipeId: 3, componentProductId: 1, componentItemNumber: 'RCP-001',
    componentDescription: 'Artisan Bread Dough (sub-recipe)', quantity: 0.3, weight: 300, volume: 0,
    sortOrder: 1, uom: 'KG', costPrice: 4.21, costContribution: 25.6, weightNet: 1730,
    isRecipe: true, children: makeBreadComponents,
    allergens: [
      { productId: 1, allergenId: 2, allergenCode: 'GLUTEN', allergenName: 'Cereals containing gluten', status: AllergenStatus.Contains, source: AllergenSource.Manual, updatedBy: 'admin', updatedAt: '2026-01-15' },
      { productId: 1, allergenId: 7, allergenCode: 'MILK', allergenName: 'Milk', status: AllergenStatus.Contains, source: AllergenSource.Manual, updatedBy: 'admin', updatedAt: '2026-01-15' },
    ], carbonFactor: null,
  },
  {
    id: 302, recipeId: 3, componentProductId: 2, componentItemNumber: 'RCP-002',
    componentDescription: 'House Tomato Sauce (sub-recipe)', quantity: 0.1, weight: 100, volume: 0,
    sortOrder: 2, uom: 'KG', costPrice: 4.21, costContribution: 8.5, weightNet: 1010,
    isRecipe: true, children: sauceComponents,
    allergens: [], carbonFactor: null,
  },
  {
    id: 303, recipeId: 3, componentProductId: 520, componentItemNumber: 'MOZ-001',
    componentDescription: 'Buffalo Mozzarella', quantity: 0.125, weight: 125, volume: 0,
    sortOrder: 3, uom: 'KG', costPrice: 14.50, costContribution: 36.8, weightNet: 125,
    isRecipe: false, allergens: [
      { productId: 520, allergenId: 7, allergenCode: 'MILK', allergenName: 'Milk', status: AllergenStatus.Contains, source: AllergenSource.Manual, updatedBy: 'admin', updatedAt: '2026-01-15' },
    ], carbonFactor: { productId: 520, kgCo2ePerUnit: 8.1, source: 'Open Food Facts', sourceReference: 'off:mozzarella', lastUpdated: '2026-03-01' },
  },
  {
    id: 304, recipeId: 3, componentProductId: 513, componentItemNumber: 'BSL-001',
    componentDescription: 'Fresh Basil', quantity: 0.01, weight: 10, volume: 0,
    sortOrder: 4, uom: 'KG', costPrice: 24.00, costContribution: 4.9, weightNet: 50,
    isRecipe: false, allergens: [], carbonFactor: { productId: 513, kgCo2ePerUnit: 0.4, source: 'Open Food Facts', sourceReference: 'off:basil', lastUpdated: '2026-03-01' },
  },
  {
    id: 305, recipeId: 3, componentProductId: 521, componentItemNumber: 'OLV-001',
    componentDescription: 'Extra Virgin Olive Oil', quantity: 0.02, weight: 18, volume: 20,
    sortOrder: 5, uom: 'L', costPrice: 12.00, costContribution: 4.9, weightNet: 920,
    isRecipe: false, allergens: [], carbonFactor: { productId: 521, kgCo2ePerUnit: 3.5, source: 'Open Food Facts', sourceReference: 'off:olive-oil', lastUpdated: '2026-03-01' },
  },
];

const breadAllergenSummary: RecipeAllergenSummary[] = [
  {
    allergenId: 2, allergenCode: 'GLUTEN', allergenName: 'Cereals containing gluten',
    status: AllergenStatus.Contains, source: AllergenSource.Manual,
    introducedBy: [{ componentId: 101, componentDescription: 'Strong White Bread Flour', depth: 1, path: 'Artisan Bread Dough > Strong White Bread Flour' }],
  },
  {
    allergenId: 7, allergenCode: 'MILK', allergenName: 'Milk',
    status: AllergenStatus.Contains, source: AllergenSource.Manual,
    introducedBy: [{ componentId: 104, componentDescription: 'Unsalted Butter', depth: 1, path: 'Artisan Bread Dough > Unsalted Butter' }],
  },
];

const pizzaAllergenSummary: RecipeAllergenSummary[] = [
  {
    allergenId: 2, allergenCode: 'GLUTEN', allergenName: 'Cereals containing gluten',
    status: AllergenStatus.Contains, source: AllergenSource.Manual,
    introducedBy: [
      { componentId: 301, componentDescription: 'Artisan Bread Dough (sub-recipe)', depth: 1, path: 'Pizza Margherita > Artisan Bread Dough > Strong White Bread Flour' },
    ],
  },
  {
    allergenId: 7, allergenCode: 'MILK', allergenName: 'Milk',
    status: AllergenStatus.Contains, source: AllergenSource.Manual,
    introducedBy: [
      { componentId: 301, componentDescription: 'Artisan Bread Dough (sub-recipe)', depth: 1, path: 'Pizza Margherita > Artisan Bread Dough > Unsalted Butter' },
      { componentId: 303, componentDescription: 'Buffalo Mozzarella', depth: 1, path: 'Pizza Margherita > Buffalo Mozzarella' },
    ],
  },
];

/**
 * V2-specific extension data for recipes that have full detail.
 * Keyed by V1 recipe id. Demo dataset trimmed to just pizza + its two sub-recipes.
 */
const v2Extensions: Record<number, Partial<RecipeV2>> = {
  102: {
    // Pizza Margherita (VAP-MARGHERITA-SALE) - yields 8 slices
    primaryCategory: 'Mains', yieldQuantity: 8, yieldUom: 'Slices',
    productionMethodId: 2, carbonScore: CarbonRating.C,
    totalCost: 4.92, costPerPortion: 0.62, allergenSummary: pizzaAllergenSummary,
    dietaryAttributes: mockDietaryAttributes['RCP-003'], aiIssueCount: 0,
    currentVersion: 5, status: RecipeStatus.Active,
    effectiveFrom: '2026-03-01', effectiveTo: null,
    createdBy: 'Head Chef', createdAt: '2025-10-15', modifiedBy: 'Sous Chef', modifiedAt: '2026-06-15',
  },
  301: {
    // Artisan Bread Dough - prepared in bulk and held as stock (batch recipe)
    recipeType: RecipeKind.Batch,
    primaryCategory: 'Bakery', yieldQuantity: 6, yieldUom: 'Loaves',
    productionMethodId: 2, carbonScore: CarbonRating.B,
    totalCost: 4.21, costPerPortion: 0.70, allergenSummary: breadAllergenSummary,
    dietaryAttributes: mockDietaryAttributes['RCP-001'] ?? [], aiIssueCount: 0,
    currentVersion: 3, status: RecipeStatus.Active,
    effectiveFrom: '2026-01-01', effectiveTo: null,
    createdBy: 'Head Baker', createdAt: '2025-11-10', modifiedBy: 'Head Baker', modifiedAt: '2026-06-01',
  },
  302: {
    // House Tomato Sauce - prepared in bulk and held as stock (batch recipe)
    recipeType: RecipeKind.Batch,
    primaryCategory: 'Sauces', yieldQuantity: 10, yieldUom: 'Portions',
    productionMethodId: 4, carbonScore: CarbonRating.A,
    totalCost: 4.21, costPerPortion: 0.42, allergenSummary: [],
    dietaryAttributes: mockDietaryAttributes['RCP-002'] ?? [], aiIssueCount: 1,
    currentVersion: 2, status: RecipeStatus.Active,
    effectiveFrom: '2026-02-01', effectiveTo: null,
    createdBy: 'Head Chef', createdAt: '2025-12-01', modifiedBy: 'Head Chef', modifiedAt: '2026-05-20',
  },
};

/** Convert V1 MockRecipe → V2 RecipeV2 by merging base data with V2 extensions */
function toRecipeV2(base: MockRecipe): RecipeV2 {
  const ext = v2Extensions[base.id] ?? {};
  return {
    id: base.id,
    franchiseId: base.franchiseId,
    itemNumber: base.itemNumber,
    description: base.description,
    status: ext.status ?? RecipeStatus.Active,
    recipeType: ext.recipeType ?? RecipeKind.Standard,
    primaryCategory: ext.primaryCategory ?? 'Uncategorised',
    yieldQuantity: ext.yieldQuantity ?? 1,
    yieldUom: ext.yieldUom ?? 'Portions',
    effectiveFrom: ext.effectiveFrom ?? '2026-01-01',
    effectiveTo: ext.effectiveTo ?? null,
    productionMethodId: ext.productionMethodId ?? 1,
    carbonScore: ext.carbonScore ?? null,
    totalCost: ext.totalCost ?? 0,
    costPerPortion: ext.costPerPortion ?? 0,
    allergenSummary: ext.allergenSummary ?? [],
    dietaryAttributes: ext.dietaryAttributes ?? [],
    aiIssueCount: ext.aiIssueCount ?? 0,
    currentVersion: ext.currentVersion ?? 1,
    createdBy: ext.createdBy ?? 'System',
    createdAt: ext.createdAt ?? '2026-01-01',
    modifiedBy: ext.modifiedBy ?? 'System',
    modifiedAt: ext.modifiedAt ?? '2026-01-01',
  };
}

/**
 * Derived V2 recipe list — built from the same mockRecipes used by the V1 screen.
 * Both pages now share the same underlying product dataset.
 */
export const mockRecipesV2: RecipeV2[] = mockRecipes.map(toRecipeV2);

const mockSteps: Record<number, RecipeStep[]> = {
  102: [
    { id: 1, recipeId: 102, stepNumber: 1, title: 'Preheat Oven', instruction: 'Preheat oven to 250°C with pizza stone inside.', productionMethodId: 2, durationMinutes: 15, temperature: '250°C', equipment: [{ id: 1, name: 'Pizza Stone' }, { id: 2, name: 'Convection Oven' }] },
    { id: 2, recipeId: 102, stepNumber: 2, title: 'Roll Dough', instruction: 'Roll dough base to 12" diameter on floured surface.', productionMethodId: 1, durationMinutes: 3, temperature: null, equipment: [{ id: 3, name: 'Rolling Pin' }, { id: 4, name: 'Flour Dusting Station' }] },
    { id: 3, recipeId: 102, stepNumber: 3, title: 'Spread Sauce', instruction: 'Spread tomato sauce evenly, leaving 1cm border.', productionMethodId: 1, durationMinutes: 1, temperature: null, equipment: [{ id: 5, name: 'Ladle' }] },
    { id: 4, recipeId: 102, stepNumber: 4, title: 'Top with Mozzarella', instruction: 'Tear mozzarella and distribute across pizza.', productionMethodId: 1, durationMinutes: 1, temperature: null, equipment: [] },
    { id: 5, recipeId: 102, stepNumber: 5, title: 'Bake Pizza', instruction: 'Bake for 8-10 minutes until crust is golden and cheese is bubbling.', productionMethodId: 2, durationMinutes: 10, temperature: '250°C', equipment: [{ id: 1, name: 'Pizza Stone' }, { id: 6, name: 'Pizza Peel' }] },
    { id: 6, recipeId: 102, stepNumber: 6, title: 'Finish & Serve', instruction: 'Drizzle with olive oil, top with fresh basil leaves. Serve immediately.', productionMethodId: 1, durationMinutes: 1, temperature: null, equipment: [] },
  ],
  301: [
    { id: 10, recipeId: 301, stepNumber: 1, title: 'Combine Dry Ingredients', instruction: 'Combine flour, salt, and yeast in large mixing bowl.', productionMethodId: 1, durationMinutes: 2, temperature: null, equipment: [{ id: 7, name: 'Stand Mixer' }, { id: 8, name: 'Mixing Bowl (Large)' }] },
    { id: 11, recipeId: 301, stepNumber: 2, title: 'Add Wet Ingredients', instruction: 'Add warm water and melted butter. Mix on low speed for 3 minutes.', productionMethodId: 1, durationMinutes: 3, temperature: '37°C', equipment: [{ id: 7, name: 'Stand Mixer' }] },
    { id: 12, recipeId: 301, stepNumber: 3, title: 'Knead Dough', instruction: 'Increase to medium speed and knead for 8 minutes until smooth and elastic.', productionMethodId: 1, durationMinutes: 8, temperature: null, equipment: [{ id: 7, name: 'Stand Mixer' }, { id: 9, name: 'Dough Hook' }] },
    { id: 13, recipeId: 301, stepNumber: 4, title: 'Prove Dough', instruction: 'Cover and prove in warm place until doubled in size.', productionMethodId: 1, durationMinutes: 60, temperature: '28°C', equipment: [{ id: 10, name: 'Proving Drawer' }] },
    { id: 14, recipeId: 301, stepNumber: 5, title: 'Shape & Rest', instruction: 'Knock back, shape into loaf or rolls. Rest 15 minutes.', productionMethodId: 1, durationMinutes: 15, temperature: null, equipment: [{ id: 11, name: 'Work Bench' }] },
  ],
  302: [
    { id: 20, recipeId: 302, stepNumber: 1, title: 'Prep Aromatics', instruction: 'Dice onion and mince garlic.', productionMethodId: 1, durationMinutes: 5, temperature: null, equipment: [{ id: 12, name: 'Chef Knife' }, { id: 13, name: 'Chopping Board' }] },
    { id: 21, recipeId: 302, stepNumber: 2, title: 'Sauté Onion', instruction: 'Sauté onion in olive oil until translucent (3-4 mins).', productionMethodId: 4, durationMinutes: 4, temperature: '160°C', equipment: [{ id: 14, name: 'Saucepan (Large)' }, { id: 15, name: 'Induction Hob' }] },
    { id: 22, recipeId: 302, stepNumber: 3, title: 'Add Garlic', instruction: 'Add garlic and cook for 30 seconds until fragrant.', productionMethodId: 4, durationMinutes: 1, temperature: '160°C', equipment: [{ id: 14, name: 'Saucepan (Large)' }] },
    { id: 23, recipeId: 302, stepNumber: 4, title: 'Simmer Sauce', instruction: 'Add chopped tomatoes, season, and simmer for 20 minutes.', productionMethodId: 4, durationMinutes: 20, temperature: '95°C', equipment: [{ id: 14, name: 'Saucepan (Large)' }] },
    { id: 24, recipeId: 302, stepNumber: 5, title: 'Blend & Finish', instruction: 'Blend to desired consistency and stir in fresh basil.', productionMethodId: 1, durationMinutes: 2, temperature: null, equipment: [{ id: 16, name: 'Stick Blender' }] },
  ],
};

/** Helper to find a recipe by id from the derived list */
const findRecipe = (id: number): RecipeV2 => mockRecipesV2.find((r) => r.id === id)!;

export const mockRecipeV2Details: Record<number, RecipeV2Detail> = {
  102: {
    ...findRecipe(102),
    components: pizzaComponents,
    allergens: pizzaAllergenSummary,
    versions: [
      {
        id: 1, recipeId: 102, versionNumber: 5, changeDescription: 'Updated mozzarella quantity from 100g to 125g',
        changedBy: 'Sous Chef', changedAt: '2026-06-15', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2026-06-16', componentSnapshot: pizzaComponents,
      },
      {
        id: 2, recipeId: 102, versionNumber: 4, changeDescription: 'Added olive oil drizzle',
        changedBy: 'Sous Chef', changedAt: '2026-05-10', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2026-05-11', componentSnapshot: [],
      },
      {
        id: 3, recipeId: 102, versionNumber: 3, changeDescription: 'Switched to buffalo mozzarella from standard',
        changedBy: 'Head Chef', changedAt: '2026-04-01', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2026-04-01', componentSnapshot: [],
      },
      {
        id: 4, recipeId: 102, versionNumber: 2, changeDescription: 'Increased basil quantity',
        changedBy: 'Sous Chef', changedAt: '2026-02-15', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2026-02-16', componentSnapshot: [],
      },
      {
        id: 5, recipeId: 102, versionNumber: 1, changeDescription: 'Initial recipe creation',
        changedBy: 'Head Chef', changedAt: '2025-10-15', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2025-10-15', componentSnapshot: [],
      },
    ],
    aiFindings: [],
    carbonTotal: 2.86,
    carbonPerPortion: 2.86,
    productionMethod: mockProductionMethods[1],
    steps: mockSteps[102] ?? [],
  },
  301: {
    ...findRecipe(301),
    components: makeBreadComponents,
    allergens: breadAllergenSummary,
    versions: [
      {
        id: 10, recipeId: 301, versionNumber: 3, changeDescription: 'Reduced butter from 75g to 50g for lighter texture',
        changedBy: 'Head Baker', changedAt: '2026-06-01', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2026-06-02', componentSnapshot: makeBreadComponents,
      },
      {
        id: 11, recipeId: 301, versionNumber: 2, changeDescription: 'Changed to sea salt from table salt',
        changedBy: 'Head Baker', changedAt: '2026-03-10', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2026-03-11', componentSnapshot: [],
      },
      {
        id: 12, recipeId: 301, versionNumber: 1, changeDescription: 'Initial recipe creation',
        changedBy: 'Head Baker', changedAt: '2025-11-10', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2025-11-10', componentSnapshot: [],
      },
    ],
    aiFindings: [],
    carbonTotal: 1.56,
    carbonPerPortion: 0.26,
    productionMethod: mockProductionMethods[1],
    steps: mockSteps[301] ?? [],
  },
  302: {
    ...findRecipe(302),
    components: sauceComponents,
    allergens: [],
    versions: [
      {
        id: 20, recipeId: 302, versionNumber: 2, changeDescription: 'Added fresh basil, reduced garlic',
        changedBy: 'Head Chef', changedAt: '2026-05-20', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2026-05-20', componentSnapshot: sauceComponents,
      },
      {
        id: 21, recipeId: 302, versionNumber: 1, changeDescription: 'Initial recipe creation',
        changedBy: 'Head Chef', changedAt: '2025-12-01', approvalStatus: ApprovalStatus.Approved,
        approvedBy: 'Head Chef', approvedAt: '2025-12-01', componentSnapshot: [],
      },
    ],
    aiFindings: [
      {
        id: 1, recipeId: 302, findingType: 'Missing Allergen', severity: AIFindingSeverity.Medium,
        description: 'Recipe contains "garlic" which is from the allium family. Consider if sulphites allergen applies to any ingredient in this recipe.',
        confidence: 0.65, status: AIFindingStatus.Open, dismissedBy: null, dismissedReason: null, createdAt: '2026-06-20',
      },
    ],
    carbonTotal: 1.83,
    carbonPerPortion: 0.18,
    productionMethod: mockProductionMethods[3],
    steps: mockSteps[302] ?? [],
  },
};

import { StockItemSearchResult, ComponentSupplierComparison } from '../../pages/RecipeV2/types';

export const mockStockItems: StockItemSearchResult[] = [
  { id: 501, itemNumber: 'FLR-001', description: 'Strong White Bread Flour', uom: 'KG', costPrice: 1.20, weightNet: 1000 },
  { id: 502, itemNumber: 'YST-001', description: 'Dried Active Yeast', uom: 'KG', costPrice: 8.50, weightNet: 500 },
  { id: 503, itemNumber: 'SLT-001', description: 'Sea Salt', uom: 'KG', costPrice: 0.85, weightNet: 1000 },
  { id: 504, itemNumber: 'BTR-001', description: 'Unsalted Butter', uom: 'KG', costPrice: 9.80, weightNet: 250 },
  { id: 505, itemNumber: 'WTR-001', description: 'Water', uom: 'L', costPrice: 0.002, weightNet: 1000 },
  { id: 510, itemNumber: 'TOM-001', description: 'Chopped Tomatoes (Tinned)', uom: 'KG', costPrice: 0.89, weightNet: 400 },
  { id: 511, itemNumber: 'GRL-001', description: 'Fresh Garlic', uom: 'KG', costPrice: 6.00, weightNet: 100 },
  { id: 512, itemNumber: 'ONI-001', description: 'White Onion', uom: 'KG', costPrice: 1.10, weightNet: 200 },
  { id: 513, itemNumber: 'BSL-001', description: 'Fresh Basil', uom: 'KG', costPrice: 24.00, weightNet: 50 },
  { id: 520, itemNumber: 'MOZ-001', description: 'Buffalo Mozzarella', uom: 'KG', costPrice: 14.50, weightNet: 125 },
  { id: 521, itemNumber: 'OLV-001', description: 'Extra Virgin Olive Oil', uom: 'L', costPrice: 12.00, weightNet: 920 },
  { id: 530, itemNumber: 'EGG-001', description: 'Egg Yolks (Pasteurised)', uom: 'KG', costPrice: 5.60, weightNet: 1000 },
  { id: 531, itemNumber: 'ANC-001', description: 'Anchovy Fillets in Oil', uom: 'KG', costPrice: 28.00, weightNet: 100 },
  { id: 532, itemNumber: 'PRM-001', description: 'Parmesan Cheese (Aged 24mo)', uom: 'KG', costPrice: 32.00, weightNet: 200 },
  { id: 540, itemNumber: 'CHO-001', description: 'Dark Chocolate 70%', uom: 'KG', costPrice: 18.50, weightNet: 1000 },
  { id: 541, itemNumber: 'SGR-001', description: 'Caster Sugar', uom: 'KG', costPrice: 1.10, weightNet: 1000 },
  { id: 542, itemNumber: 'VNL-001', description: 'Vanilla Extract', uom: 'L', costPrice: 45.00, weightNet: 500 },
  { id: 543, itemNumber: 'CRM-001', description: 'Double Cream', uom: 'L', costPrice: 4.20, weightNet: 1000 },
];

export const mockSupplierComparisons: Record<number, ComponentSupplierComparison[]> = {
  102: [
    {
      componentId: 301,
      componentDescription: 'Artisan Bread Dough (sub-recipe)',
      suppliers: [],
    },
    {
      componentId: 303,
      componentDescription: 'Buffalo Mozzarella',
      suppliers: [
        { supplierId: 1, supplierName: 'Campania Fresh Ltd', itemPrice: 3.63, packSize: 250, packUom: 'g', costPerUnit: 14.50, isPreferred: true },
        { supplierId: 2, supplierName: 'Italian Imports Co', itemPrice: 4.20, packSize: 300, packUom: 'g', costPerUnit: 14.00, isPreferred: false },
        { supplierId: 3, supplierName: 'Wholesale Dairy', itemPrice: 11.80, packSize: 1000, packUom: 'g', costPerUnit: 11.80, isPreferred: false },
      ],
    },
    {
      componentId: 304,
      componentDescription: 'Fresh Basil',
      suppliers: [
        { supplierId: 4, supplierName: 'Green Herbs Direct', itemPrice: 1.20, packSize: 50, packUom: 'g', costPerUnit: 24.00, isPreferred: true },
        { supplierId: 5, supplierName: 'Farm Fresh Wholesale', itemPrice: 4.50, packSize: 200, packUom: 'g', costPerUnit: 22.50, isPreferred: false },
      ],
    },
    {
      componentId: 305,
      componentDescription: 'Extra Virgin Olive Oil',
      suppliers: [
        { supplierId: 6, supplierName: 'Mediterranean Oils', itemPrice: 12.00, packSize: 1000, packUom: 'ml', costPerUnit: 12.00, isPreferred: true },
        { supplierId: 7, supplierName: 'Bulk Oils Ltd', itemPrice: 48.00, packSize: 5000, packUom: 'ml', costPerUnit: 9.60, isPreferred: false },
      ],
    },
  ],
};
